import { FC, useCallback } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import { CostType, ProviderType } from "@/services/cloudchipr.api";
import {
  costTypes,
  getTitleByCostTypeAndProvider,
} from "@/components/pages/resource-explorer/components/settings-panel/components/cost-type-settings-section/utils/constants";
import { CostTypeSelectItem } from "@/components/pages/resource-explorer/components/settings-panel/components/cost-type-settings-section/CostTypeSelectItem";
import { CostTypeSelectItemProviders } from "@/components/pages/resource-explorer/components/settings-panel/components/cost-type-settings-section/CostTypeSelectItemProviders";

interface CostTypeSelectProps {
  costType: CostType;
  onCostTypeChange(costType: CostType): void;
  size?: "small" | "medium";
  workFor?: Extract<ProviderType, "aws" | "azure">;
}

export const CostTypeSelect: FC<CostTypeSelectProps> = ({
  costType,
  workFor,
  size = "medium",
  onCostTypeChange,
}) => {
  const renderValue = useCallback(
    (value: CostType) => {
      const providers = costTypes[value]?.providers;
      const label = getTitleByCostTypeAndProvider(
        costTypes[value].label,
        value,
        workFor,
      );

      return (
        <Stack direction="row" spacing={1} height={24} alignItems="center">
          <Typography fontSize={size === "medium" ? 16 : 14}>
            {label} Costs
          </Typography>

          {!workFor && (
            <CostTypeSelectItemProviders
              providers={providers ?? []}
              hideTooltip
            />
          )}
        </Stack>
      );
    },
    [workFor, size],
  );

  const changeHandler = useCallback(
    (event: SelectChangeEvent<CostType>) => {
      const newCostType = event.target.value as CostType;
      if (costType === newCostType) {
        return;
      }

      onCostTypeChange(newCostType);
    },
    [onCostTypeChange, costType],
  );

  return (
    <FormControl fullWidth size="xsmall">
      <InputLabel>Cost Type</InputLabel>
      <Select
        fullWidth
        label="Cost type"
        renderValue={renderValue}
        value={costType}
        onChange={changeHandler}
      >
        {Object.entries(costTypes)
          .filter(([_, data]) => !workFor || data.providers.includes(workFor))
          .map(([key, data]) => {
            return (
              <MenuItem
                dense
                value={key}
                key={key}
                sx={{
                  py: 1,
                }}
              >
                <CostTypeSelectItem {...data} value={key} workFor={workFor} />
              </MenuItem>
            );
          })}
      </Select>
    </FormControl>
  );
};
